import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { 
  HiOutlineInbox,
  HiOutlineEye,
  HiOutlineUserCircle,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineArrowPath,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineBell
} from 'react-icons/hi2'
import { cn } from '../../utils/formatter'
import { demandaService, notificacaoService } from '../../services'
import { toast } from '../../components/Toast'
import { useAuth } from '../../contexts/AuthContext'
import { ROUTES } from '../../routes/constants'
import type { Demanda, ObservadorData, Notificacao } from '../../models'
import { GlassHoverEffect } from '../../components/GlassHoverEffect'
import { SimpleSelect } from '../../components/Select/SimpleSelect'

type NotificationType = 'email' | 'observer'

type UnifiedNotification = {
  id: string
  demandId: number
  title: string
  message: string
  createdAt: string
  read: boolean
  type: NotificationType
  demanda?: Demanda
  notificacao?: Notificacao
}

type NotificationFilter = 'all' | 'observer' | 'assigned' | 'solved' | 'inprogress'
type ReadStatusTab = 'unread' | 'all'
type DateFilter = 'all' | 'today' | 'week' | 'month'

// Chave para localStorage
const VIEWED_OBSERVERS_KEY = 'demandas_viewed_observers'

// Utilitário para gerenciar notificações de observador visualizadas
const viewedObserversManager = {
  get(): Set<number> {
    try {
      const stored = localStorage.getItem(VIEWED_OBSERVERS_KEY)
      if (!stored) return new Set()
      return new Set(JSON.parse(stored))
    } catch {
      return new Set()
    }
  },
  
  add(demandaId: number): void {
    const viewed = this.get()
    viewed.add(demandaId)
    localStorage.setItem(VIEWED_OBSERVERS_KEY, JSON.stringify([...viewed]))
  },
  
  remove(demandaId: number): void {
    const viewed = this.get()
    viewed.delete(demandaId)
    localStorage.setItem(VIEWED_OBSERVERS_KEY, JSON.stringify([...viewed]))
  },
  
  has(demandaId: number): boolean {
    return this.get().has(demandaId)
  },
}

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all')
  const [readStatusTab, setReadStatusTab] = useState<ReadStatusTab>('unread')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const { user } = useAuth()
  const navigate = useNavigate()

  // Função auxiliar para verificar se demanda está atribuída ao usuário
  const isAssignedToMe = useCallback((demanda?: Demanda): boolean => {
    if (!demanda || !user?.id) return false
    
    if (demanda.atribuidas && demanda.atribuidas.length > 0) {
      return demanda.atribuidas.some(
        atribuida => atribuida.usuario_fk === user.id && atribuida.active
      )
    }
    
    if (demanda.demandado_fk === user.id) return true
    
    if (demanda.demandados && demanda.demandados.length > 0) {
      return demanda.demandados.some(d => d.id === user.id)
    }
    
    return false
  }, [user?.id])

  // Filtra por data
  const filterByDate = useCallback((notification: UnifiedNotification): boolean => {
    if (dateFilter === 'all') return true
    
    const notifDate = new Date(notification.createdAt)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - notifDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    switch (dateFilter) {
      case 'today':
        return diffDays <= 1
      case 'week':
        return diffDays <= 7
      case 'month':
        return diffDays <= 30
      default:
        return true
    }
  }, [dateFilter])

  // Filtra por busca
  const filterBySearch = useCallback((notification: UnifiedNotification): boolean => {
    if (!searchQuery) return true
    
    const query = searchQuery.toLowerCase()
    const title = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()
    
    return title.includes(query) || message.includes(query)
  }, [searchQuery])

  // Filtrar notificações com todos os filtros
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      // Filtro de status de leitura (aba)
      if (readStatusTab === 'unread' && notification.read) return false
      
      // Filtro de tipo/categoria
      if (activeFilter === 'observer' && notification.type !== 'observer') return false
      
      if (activeFilter === 'assigned' && !isAssignedToMe(notification.demanda)) return false
      
      if (activeFilter === 'solved' && notification.demanda?.status !== 'SOLUCIONADA') return false
      
      if (activeFilter === 'inprogress' && notification.demanda?.status !== 'EMPROGRESSO') return false
      
      // Filtro de status da demanda (adicional)
      if (statusFilter !== 'all' && notification.demanda?.status !== statusFilter) return false
      
      // Filtro de data
      if (!filterByDate(notification)) return false
      
      // Filtro de busca
      if (!filterBySearch(notification)) return false
      
      return true
    })
  }, [notifications, readStatusTab, activeFilter, statusFilter, isAssignedToMe, filterByDate, filterBySearch])

  const unreadCount = notifications.filter(n => !n.read).length
  const unreadObserverCount = notifications.filter(n => n.type === 'observer' && !n.read).length
  const unreadAssignedCount = notifications.filter(n => !n.read && isAssignedToMe(n.demanda)).length
  const unreadSolvedCount = notifications.filter(n => !n.read && n.demanda?.status === 'SOLUCIONADA').length
  const unreadInProgressCount = notifications.filter(n => !n.read && n.demanda?.status === 'EMPROGRESSO').length

  const getTimeLabel = useCallback((date?: string) => {
    if (!date) return 'Atualizado recentemente'

    try {
      const parsed = new Date(date)
      if (Number.isNaN(parsed.getTime())) return 'Atualizado recentemente'
      return formatDistanceToNow(parsed, {
        addSuffix: true,
        locale: ptBR,
      })
    } catch {
      return 'Atualizado recentemente'
    }
  }, [])

  // Remove tags HTML do texto
  const stripHtml = useCallback((html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }, [])

  const getObserverCreatedAt = useCallback((observer?: ObservadorData) => {
    if (!observer) return undefined

    if ('created_at' in observer && observer.created_at) {
      return observer.created_at
    }

    if ('updated_at' in observer && observer.updated_at) {
      return observer.updated_at
    }

    if ('pivot' in observer && observer.pivot) {
      if (observer.pivot.created_at) {
        return observer.pivot.created_at
      }

      if (observer.pivot.updated_at) {
        return observer.pivot.updated_at
      }

      const pivotAny = observer.pivot as { createdAt?: string; updatedAt?: string }
      return pivotAny.createdAt ?? pivotAny.updatedAt
    }

    return undefined
  }, [])

  const findObserverEntry = useCallback((demanda: Demanda): ObservadorData | undefined => {
    if (!user?.id || !demanda.observadores) {
      return undefined
    }

    return demanda.observadores.find((observer) => {
      if ('observador_fk' in observer) {
        if (observer.observador_fk !== user.id) return false
        if (typeof observer.active === 'boolean' && !observer.active) return false
        return true
      }

      if ('pivot' in observer && observer.pivot) {
        if (observer.pivot.observador_fk !== user.id) return false
        if (typeof observer.pivot.active === 'boolean' && !observer.pivot.active) return false
        return true
      }

      return false
    })
  }, [user?.id])

  const shouldIgnoreDemand = useCallback((demanda: Demanda): boolean => {
    const finishedStatuses = ['FINALIZADA', 'SOLUCIONADA', 'ERRADA']
    return finishedStatuses.includes(demanda.status?.toUpperCase() || '')
  }, [])

  const mapObserverNotifications = useCallback((demandas: Demanda[]): UnifiedNotification[] => {
    const mapped = demandas.reduce<UnifiedNotification[]>((acc, demanda) => {
      if (!demanda.id) return acc

      if (shouldIgnoreDemand(demanda)) {
        viewedObserversManager.remove(demanda.id)
        return acc
      }

      const observerEntry = findObserverEntry(demanda)
      const createdAt = getObserverCreatedAt(observerEntry) ?? demanda.created_at ?? demanda.updated_at
      const sectorName = demanda.setor?.nome_setor ?? demanda.processo?.setor?.nome_setor
      const details = [
        sectorName ? `Setor: ${sectorName}` : null,
        demanda.prioridade ? `Prioridade: ${demanda.prioridade}` : null,
        demanda.status ? `Status: ${demanda.status}` : null,
      ]
        .filter(Boolean)
        .join(' • ')

      const wasViewed = viewedObserversManager.has(demanda.id)

      const notification: UnifiedNotification = {
        id: `observer-${demanda.id}`,
        demandId: demanda.id,
        title: demanda.titulo ?? 'Demanda sem título',
        message: details || 'Você foi adicionado como observador desta demanda.',
        read: wasViewed,
        type: 'observer',
        createdAt: createdAt || new Date().toISOString(),
        demanda,
      }

      acc.push(notification)
      return acc
    }, [])

    return mapped.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA
    })
  }, [findObserverEntry, getObserverCreatedAt, shouldIgnoreDemand])

  const mapEmailNotifications = useCallback((notificacoes: Notificacao[]): UnifiedNotification[] => {
    return notificacoes.map(notif => {
      const unified: UnifiedNotification = {
        id: `email-${notif.id}`,
        demandId: notif.demanda_fk,
        title: notif.titulo,
        message: notif.comentario || 'Nova atualização na demanda',
        createdAt: notif.created_at,
        read: notif.visualizado,
        type: 'email',
        notificacao: notif,
      }
      
      if (notif.demanda) {
        unified.demanda = notif.demanda
      }
      
      return unified
    }).sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA
    })
  }, [])

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    try {
      // Busca TODAS as notificações sempre (incluindo lidas e não lidas)
      const [emailNotifs, demandas] = await Promise.all([
        notificacaoService.getTodasNotificacoes(),
        demandaService.getObservaveis()
      ])

      const observerNotifs = mapObserverNotifications(demandas)
      const emailMapped = mapEmailNotifications(emailNotifs)

      // Combina e ordena por data
      const combined = [...observerNotifs, ...emailMapped].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA
      })

      setNotifications(combined)
    } catch (error) {
      console.error('Erro ao buscar notificações:', error)
      toast.error('Não foi possível carregar as notificações.')
    } finally {
      setIsLoading(false)
    }
  }, [user?.id, mapObserverNotifications, mapEmailNotifications])

  // Carrega notificações apenas ao montar (não recarrega ao trocar de aba)
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const getActiveIndex = () => {
    if (filteredNotifications.length === 0) return 0
    const firstUnreadIndex = filteredNotifications.findIndex((notification) => !notification.read)
    return firstUnreadIndex !== -1 ? firstUnreadIndex : 0
  }

  // Contadores dinâmicos baseados na aba ativa
  const getFilterCount = useCallback((filter: NotificationFilter) => {
    if (readStatusTab === 'unread') {
      // Na aba "Não Lidas", mostrar apenas não lidas
      switch (filter) {
        case 'all': return unreadCount
        case 'observer': return unreadObserverCount
        case 'assigned': return unreadAssignedCount
        case 'solved': return unreadSolvedCount
        case 'inprogress': return unreadInProgressCount
        default: return 0
      }
    } else {
      // Na aba "Todas", mostrar total (lidas + não lidas)
      switch (filter) {
        case 'all': return notifications.length
        case 'observer': return notifications.filter(n => n.type === 'observer').length
        case 'assigned': return notifications.filter(n => isAssignedToMe(n.demanda)).length
        case 'solved': return notifications.filter(n => n.demanda?.status === 'SOLUCIONADA').length
        case 'inprogress': return notifications.filter(n => n.demanda?.status === 'EMPROGRESSO').length
        default: return 0
      }
    }
  }, [readStatusTab, unreadCount, unreadObserverCount, unreadAssignedCount, unreadSolvedCount, unreadInProgressCount, notifications, isAssignedToMe])

  // Tabs de filtro de tipo
  const filterTabs: Array<{ value: NotificationFilter; label: string; icon: React.ReactNode }> = [
    { value: 'all', label: 'Todas', icon: <HiOutlineInbox className="w-4 h-4" /> },
    { value: 'observer', label: 'Observador', icon: <HiOutlineEye className="w-4 h-4" /> },
    { value: 'assigned', label: 'Atribuídas', icon: <HiOutlineUserCircle className="w-4 h-4" /> },
    { value: 'solved', label: 'Solucionadas', icon: <HiOutlineCheckCircle className="w-4 h-4" /> },
    { value: 'inprogress', label: 'Em Progresso', icon: <HiOutlineClock className="w-4 h-4" /> },
  ]

  const handleViewDemanda = async (notification: UnifiedNotification) => {
    if (!notification.demandId) return
    
    // Se for notificação de email, marca como visualizada no backend
    if (notification.type === 'email' && notification.notificacao && !notification.read) {
      try {
        await notificacaoService.marcarComoVisualizada(notification.notificacao.id)
      } catch (err) {
        console.error('Erro ao marcar notificação como visualizada:', err)
      }
    }
    
    // Se for notificação de observador, marca como lida no localStorage
    if (notification.type === 'observer' && !notification.read) {
      viewedObserversManager.add(notification.demandId)
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      )
    }
    
  navigate(ROUTES.DEMANDS.VIEW(String(notification.demandId), { type: 'all' }))
  }

  const handleRefuseObserver = async (notification: UnifiedNotification) => {
    if (processingId) return

    setProcessingId(notification.id)
    try {
      await demandaService.recusarObservador(notification.demandId)
      
      // Remove do localStorage também
      viewedObserversManager.remove(notification.demandId)
      
      setNotifications((prev) => prev.filter((item) => item.id !== notification.id))
      toast.success('Você não receberá mais atualizações desta demanda.')
      
      // Atualiza a lista
      fetchNotifications()
    } catch (error) {
      console.error('Erro ao recusar observador:', error)
      toast.error('Não foi possível recusar a observação. Tente novamente.')
    } finally {
      setProcessingId(null)
    }
  }

  const handleMarkAsRead = async (notification: UnifiedNotification) => {
    if (notification.type === 'email' && notification.notificacao && !notification.read) {
      try {
        await notificacaoService.marcarComoVisualizada(notification.notificacao.id)
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        )
        // toast.success('Notificação marcada como lida.')
      } catch (error) {
        console.error('Erro ao marcar notificação:', error)
        toast.error('Não foi possível marcar como lida.')
      }
    } else if (notification.type === 'observer' && !notification.read) {
      viewedObserversManager.add(notification.demandId)
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      )
      // toast.success('Notificação marcada como lida.')
    }
  }

  // Funções para seleção em massa
  const handleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredNotifications.map(n => n.id)))
    }
  }

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecione ao menos uma notificação.')
      return
    }

    try {
      const selectedNotifications = filteredNotifications.filter(n => selectedIds.has(n.id))
      const emailNotificationIds = selectedNotifications
        .filter(n => n.type === 'email' && n.notificacao && !n.read)
        .map(n => n.notificacao!.id)
      
      const observerNotifications = selectedNotifications.filter(n => n.type === 'observer' && !n.read)

      if (emailNotificationIds.length > 0) {
        await notificacaoService.marcarVariasComoVisualizadas(emailNotificationIds)
      }

      observerNotifications.forEach(n => {
        viewedObserversManager.add(n.demandId)
      })

      setNotifications(prev =>
        prev.map(n => selectedIds.has(n.id) ? { ...n, read: true } : n)
      )

      toast.success(`${selectedIds.size} notificação(ões) marcada(s) como lida(s).`)
      setSelectedIds(new Set())
    } catch (error) {
      console.error('Erro ao marcar notificações:', error)
      toast.error('Não foi possível marcar todas as notificações.')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const unreadEmailNotifications = notifications.filter(n => n.type === 'email' && !n.read && n.notificacao)
      const unreadObserverNotifications = notifications.filter(n => n.type === 'observer' && !n.read)

      if (unreadEmailNotifications.length > 0) {
        await notificacaoService.marcarTodasComoVisualizadas()
      }

      unreadObserverNotifications.forEach(n => {
        viewedObserversManager.add(n.demandId)
      })

      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      toast.success('Todas as notificações foram marcadas como lidas.')
      setSelectedIds(new Set())
    } catch (error) {
      console.error('Erro ao marcar todas as notificações:', error)
      toast.error('Não foi possível marcar todas as notificações.')
    }
  }

  return (
    <div className="min-h-[calc(100vh-103px)] flex flex-col px-6 py-4 overflow-hidden">
      <div className="w-full flex flex-col h-full max-h-full">
        {/* Header Centralizado */}
        <div className="text-center mb-3 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Notificações
          </h1>
          
          {/* Navigation com GlassHoverEffect */}
          <div className="flex justify-center">
            <GlassHoverEffect 
              activeIndex={readStatusTab === 'unread' ? 0 : 1}
              direction="horizontal"
            >
              {({ containerRef, containerProps, getItemProps }) => (
                <nav 
                  ref={containerRef}
                  className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-1 flex space-x-1 border border-gray-200 dark:border-neutral-700"
                  {...containerProps}
                >
                  <button
                    type="button"
                    {...getItemProps(0)}
                    onClick={() => setReadStatusTab('unread')}
                    className={cn(
                      'px-6 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 flex items-center gap-2 relative z-10',
                      readStatusTab === 'unread'
                        ? 'text-blue-700 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-neutral-700'
                    )}
                  >
                    <HiOutlineBell className="w-4 h-4" />
                    Não Lidas
                    {unreadCount > 0 && (
                      <span className={cn(
                        'ml-1 px-2 py-0.5 rounded-full text-xs font-bold',
                        readStatusTab === 'unread'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400'
                      )}>
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    {...getItemProps(1)}
                    onClick={() => setReadStatusTab('all')}
                    className={cn(
                      'px-6 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 flex items-center gap-2 relative z-10',
                      readStatusTab === 'all'
                        ? 'text-blue-700 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-neutral-700'
                    )}
                  >
                    <HiOutlineInbox className="w-4 h-4" />
                    Todas
                    <span className={cn(
                      'ml-1 px-2 py-0.5 rounded-full text-xs font-bold',
                      readStatusTab === 'all'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400'
                    )}>
                      {notifications.length}
                    </span>
                  </button>
                </nav>
              )}
            </GlassHoverEffect>
          </div>
        </div>

        {/* Filtros Compactos - Uma Linha */}
        <div className="mb-2 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 p-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {/* Busca Compacta */}
            <div className="flex-1 relative">
              <HiOutlineMagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Filtros de Tipo - Horizontal */}
            {filterTabs.map((tab) => {
              const count = getFilterCount(tab.value)
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveFilter(tab.value)}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all duration-200 whitespace-nowrap',
                    activeFilter === tab.value
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700'
                  )}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={cn(
                      'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                      activeFilter === tab.value
                        ? 'bg-white/20'
                        : 'bg-gray-200 dark:bg-neutral-600'
                    )}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}

            {/* Filtro de Status da Demanda */}
            <SimpleSelect
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as string)}
              options={[
                { value: 'all', label: 'Status' },
                { value: 'RECEBIDA', label: 'Recebida' },
                { value: 'ATRIBUIDA', label: 'Atribuída' },
                { value: 'EMPROGRESSO', label: 'Em Progresso' },
                { value: 'SOLUCIONADA', label: 'Solucionada' },
                { value: 'FINALIZADA', label: 'Finalizada' },
              ]}
              className="min-w-[120px]"
            />

            {/* Data e Atualizar */}
            <SimpleSelect
              value={dateFilter}
              onChange={(value) => setDateFilter(value as DateFilter)}
              options={[
                { value: 'all', label: 'Período' },
                { value: 'today', label: 'Hoje' },
                { value: 'week', label: 'Semana' },
                { value: 'month', label: 'Mês' },
              ]}
              className="min-w-[120px]"
            />
            
            <button
              type="button"
              onClick={fetchNotifications}
              disabled={isLoading}
              className={cn(
                'p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors',
                isLoading && 'cursor-wait opacity-60'
              )}
              title="Atualizar"
            >
              <HiOutlineArrowPath className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Ações em Massa */}
        {filteredNotifications.length > 0 && (
          <div className="mb-2 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 p-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === filteredNotifications.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {selectedIds.size > 0 ? `${selectedIds.size} selecionada(s)` : 'Selecionar todas'}
                  </span>
                </label>
              </div>

              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleMarkSelectedAsRead}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
                  >
                    <HiOutlineCheckCircle className="w-4 h-4" />
                    Marcar selecionadas como lidas
                  </button>
                </div>
              )}

              {selectedIds.size === 0 && unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                >
                  <HiOutlineCheckCircle className="w-4 h-4" />
                  Marcar todas como lidas
                </button>
              )}
            </div>
          </div>
        )}

        {/* Lista de Notificações - ROLÁVEL */}
        <div className="min-h-[calc(100vh-330px)] flex-1 overflow-hidden bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700">
          <div className="h-full overflow-y-auto p-3">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={`notification-skeleton-${index}`}
                    className="h-24 rounded-lg bg-gray-100/80 dark:bg-neutral-700/60 animate-pulse"
                  />
                ))}
              </div>
            ) : filteredNotifications.length > 0 ? (
              <GlassHoverEffect
                activeIndex={getActiveIndex()}
                direction="vertical"
                className="flex flex-col gap-2 relative"
                glassClassName="rounded-lg shadow-sm bg-blue-50/90 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/40 backdrop-blur-sm"
              >
                {({ containerRef, containerProps, getItemProps }) => (
                  <div
                    ref={containerRef as React.RefObject<HTMLDivElement>}
                    className="flex flex-col gap-2 relative"
                    {...containerProps}
                  >
                    {filteredNotifications.map((notification, index) => {
                      // Informações de quem criou
                      const creatorName = notification.notificacao?.editor?.name || 
                                         notification.demanda?.criador?.name || 
                                         notification.demanda?.criado_por?.name ||
                                         'Sistema'
                      const creatorInitials = creatorName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                      
                      // Informações da demanda
                      const demandaStatus = notification.demanda?.status
                      const demandaPrioridade = notification.demanda?.prioridade
                      const demandaSetor = notification.demanda?.setor?.nome_setor || notification.demanda?.processo?.setor?.nome_setor
                      
                      return (
                        <div
                          key={notification.id}
                          {...getItemProps(index)}
                          className={cn(
                            'group relative px-3 py-2 hover:bg-gray-50/50 dark:hover:bg-neutral-700/30 transition-all duration-200 border-l-2 rounded-lg cursor-pointer',
                            notification.read
                              ? 'border-transparent opacity-60 hover:opacity-100'
                              : 'border-blue-500/40 bg-blue-50/30 dark:bg-blue-900/10'
                          )}
                          onClick={() => handleViewDemanda(notification)}
                        >
                          <div className="flex items-center gap-3">
                            {/* Checkbox para seleção */}
                            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(notification.id)}
                                onChange={() => handleSelectOne(notification.id)}
                                className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                              />
                            </div>

                            {/* Avatar Compacto */}
                            <div className={cn(
                              "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
                              notification.read
                                ? "bg-gray-200 dark:bg-neutral-700 text-gray-500 dark:text-gray-400"
                                : "bg-blue-500 text-white"
                            )}>
                              {creatorInitials}
                            </div>

                            {/* Coluna 1: Título e Mensagem */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
                                  {notification.title}
                                </h3>
                                {notification.type === 'observer' && (
                                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                    Observador
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                                {stripHtml(notification.message)}
                              </p>
                            </div>

                            {/* Coluna 2: Badges */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {demandaPrioridade && (
                                <span className={cn(
                                  "px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap",
                                  demandaPrioridade === 'Urgente' && "bg-red-500 text-white",
                                  demandaPrioridade === 'Alta' && "bg-orange-500 text-white",
                                  demandaPrioridade === 'Media' && "bg-yellow-500 text-white",
                                  demandaPrioridade === 'Baixa' && "bg-gray-400 text-white"
                                )}>
                                  {demandaPrioridade}
                                </span>
                              )}
                              {demandaStatus && (
                                <span className={cn(
                                  "px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap",
                                  demandaStatus === 'SOLUCIONADA' && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
                                  demandaStatus === 'EMPROGRESSO' && "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400",
                                  demandaStatus === 'RECEBIDA' && "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
                                  demandaStatus === 'ATRIBUIDA' && "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
                                  !['SOLUCIONADA', 'EMPROGRESSO', 'RECEBIDA', 'ATRIBUIDA'].includes(demandaStatus) && "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                )}>
                                  {demandaStatus}
                                </span>
                              )}
                              {demandaSetor && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap max-w-[150px] truncate" title={demandaSetor}>
                                  {demandaSetor}
                                </span>
                              )}
                            </div>

                            {/* Coluna 3: Autor e Tempo */}
                            <div className="flex flex-col items-end flex-shrink-0 min-w-[120px]">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-full" title={creatorName}>
                                {creatorName}
                              </span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {getTimeLabel(notification.createdAt)}
                              </span>
                            </div>

                            {/* Coluna 4: Ações */}
                            <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                              {!notification.read && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleMarkAsRead(notification)
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors"
                                  title="Marcar como lida"
                                >
                                  <HiOutlineCheckCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                              )}
                              
                              {notification.type === 'observer' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRefuseObserver(notification)
                                  }}
                                  disabled={processingId === notification.id}
                                  className={cn(
                                    "p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors",
                                    processingId === notification.id && "opacity-50 cursor-wait"
                                  )}
                                  title="Recusar observação"
                                >
                                  {processingId === notification.id ? (
                                    <HiOutlineArrowPath className="w-4 h-4 text-gray-400 animate-spin" />
                                  ) : (
                                    <HiOutlineXMark className="w-4 h-4 text-red-600 dark:text-red-400" />
                                  )}
                                </button>
                              )}

                              {!notification.read && (
                                <span className="inline-flex h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </GlassHoverEffect>
            ) : (
              <div className="min-h-[615px] flex flex-col items-center justify-center h-full px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                <HiOutlineInbox className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">
                  {!user 
                    ? 'Faça login para visualizar suas notificações.'
                    : notifications.length === 0
                      ? 'Nenhuma notificação no momento.'
                      : searchQuery
                        ? `Nenhuma notificação encontrada para "${searchQuery}"`
                        : activeFilter === 'observer'
                          ? 'Nenhuma notificação de observador no momento.'
                          : activeFilter === 'assigned'
                            ? 'Nenhuma demanda atribuída a você no momento.'
                            : activeFilter === 'solved'
                              ? 'Nenhuma demanda solucionada no momento.'
                              : activeFilter === 'inprogress'
                                ? 'Nenhuma demanda em progresso no momento.'
                                : 'Nenhuma notificação nesta categoria.'
                  }
                </p>
                {(searchQuery || dateFilter !== 'all' || activeFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('')
                      setDateFilter('all')
                      setActiveFilter('all')
                    }}
                    className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
